import api from './axiosConfig';
import {
  LeaveTypeDto,
  LeaveTypeConfigurationDto,
  SaveLeaveTypeConfiguration,
  CreateLeaveType,
  UpdateLeaveType,
  LeavePeriodDto,
  CreateLeavePeriod,
  UpdateLeavePeriod,
  CreateLeaveEntitlement,
  CreateLeaveEntitlementsResponse,
  BulkCreateLeaveEntitlement,
  UpdateLeaveEntitlement,
  LeaveRequestDto,
  CreateLeaveRequest,
  AssignLeave,
  HolidayDto,
  CreateHoliday,
  UpdateHoliday,
  LeaveUsageReportDto,
  LeaveBalanceDto,
  WhoIsOnLeaveDto,
  LeaveEntitlementListResponse,
} from '../types';

export const leaveApi = {
  // ─── Leave Types ──────────────────────────────────────────
  getLeaveTypes: async (): Promise<LeaveTypeDto[]> => {
    const response = await api.get<LeaveTypeDto[]>('/leave/types');
    return response.data;
  },

  getLeaveTypeById: async (id: string): Promise<LeaveTypeDto> => {
    const response = await api.get<LeaveTypeDto>(`/leave/types/${id}`);
    return response.data;
  },

  createLeaveType: async (data: CreateLeaveType): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/leave/types', data);
    return response.data;
  },

  updateLeaveType: async (id: string, data: UpdateLeaveType): Promise<void> => {
    await api.put(`/leave/types/${id}`, data);
  },

  deleteLeaveType: async (id: string): Promise<void> => {
    await api.delete(`/leave/types/${id}`);
  },

  getLeaveTypeConfiguration: async (leaveTypeId: string): Promise<LeaveTypeConfigurationDto> => {
    const response = await api.get<LeaveTypeConfigurationDto>(`/leave/types/${leaveTypeId}/configuration`);
    return response.data;
  },

  saveLeaveTypeConfiguration: async (leaveTypeId: string, data: SaveLeaveTypeConfiguration): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>(`/leave/types/${leaveTypeId}/configuration`, data);
    return response.data;
  },

  // ─── Leave Periods ────────────────────────────────────────
  getLeavePeriods: async (): Promise<LeavePeriodDto[]> => {
    const response = await api.get<LeavePeriodDto[]>('/leave/periods');
    return response.data;
  },

  createLeavePeriod: async (data: CreateLeavePeriod): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/leave/periods', data);
    return response.data;
  },

  updateLeavePeriod: async (id: string, data: UpdateLeavePeriod): Promise<void> => {
    await api.put(`/leave/periods/${id}`, data);
  },

  deleteLeavePeriod: async (id: string): Promise<void> => {
    await api.delete(`/leave/periods/${id}`);
  },

  // ─── Leave Entitlements ───────────────────────────────────
  getLeaveEntitlements: async (params?: {
    employeeId?: string;
    leaveTypeId?: string;
    leavePeriodId?: string;
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<LeaveEntitlementListResponse> => {
    const response = await api.get<LeaveEntitlementListResponse>('/leave/entitlements', { params });
    return response.data;
  },

  createLeaveEntitlement: async (data: CreateLeaveEntitlement): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/leave/entitlements', data);
    return response.data;
  },

  createLeaveEntitlements: async (data: CreateLeaveEntitlement[]): Promise<CreateLeaveEntitlementsResponse> => {
    const response = await api.post<CreateLeaveEntitlementsResponse>('/leave/entitlements', data);
    return response.data;
  },

  bulkCreateLeaveEntitlement: async (data: BulkCreateLeaveEntitlement): Promise<{ count: number }> => {
    const response = await api.post<{ count: number }>('/leave/entitlements/bulk', data);
    return response.data;
  },

  updateLeaveEntitlement: async (id: string, data: UpdateLeaveEntitlement): Promise<void> => {
    await api.put(`/leave/entitlements/${id}`, data);
  },

  deleteLeaveEntitlement: async (id: string): Promise<void> => {
    await api.delete(`/leave/entitlements/${id}`);
  },

  // ─── Leave Requests ───────────────────────────────────────
  getLeaveRequests: async (params?: {
    employeeId?: string;
    leaveTypeId?: string;
    leavePeriodId?: string;
    status?: number;
    fromDate?: string;
    toDate?: string;
    searchTerm?: string;
  }): Promise<LeaveRequestDto[]> => {
    const response = await api.get<LeaveRequestDto[]>('/leave/requests', { params });
    return response.data;
  },

  getLeaveRequestById: async (id: string): Promise<LeaveRequestDto> => {
    const response = await api.get<LeaveRequestDto>(`/leave/requests/${id}`);
    return response.data;
  },

  createLeaveRequest: async (data: CreateLeaveRequest): Promise<{ id: string }> => {
    const formData = new FormData();
    if (data.employeeId) formData.append('employeeId', data.employeeId);
    formData.append('leaveTypeId', data.leaveTypeId);
    formData.append('leavePeriodId', data.leavePeriodId);
    formData.append('startDate', data.startDate);
    formData.append('endDate', data.endDate);
    formData.append('numberOfDays', String(data.numberOfDays));
    formData.append('isHalfDay', String(data.isHalfDay));
    if (data.halfDayType) formData.append('halfDayType', data.halfDayType);
    formData.append('reason', data.reason);
    if (data.attachment) formData.append('attachment', data.attachment);

    const response = await api.post<{ id: string }>('/leave/requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadLeaveAttachment: async (id: string): Promise<Blob> => {
    const response = await api.get(`/leave/requests/${id}/attachment`, {
      responseType: 'blob',
    });
    return response.data;
  },

  approveLeaveRequest: async (id: string, data: { adminRemarks?: string; approvedById: string }): Promise<void> => {
    await api.post(`/leave/requests/${id}/approve`, data);
  },

  rejectLeaveRequest: async (id: string, data: { adminRemarks?: string; rejectedById: string }): Promise<void> => {
    await api.post(`/leave/requests/${id}/reject`, data);
  },

  cancelLeaveRequest: async (id: string): Promise<void> => {
    await api.post(`/leave/requests/${id}/cancel`);
  },

  bulkApproveLeaveRequests: async (data: { ids: string[]; approvedById: string; adminRemarks?: string }): Promise<{ count: number }> => {
    const response = await api.post<{ count: number }>('/leave/requests/bulk-approve', data);
    return response.data;
  },

  bulkRejectLeaveRequests: async (data: { ids: string[]; rejectedById: string; adminRemarks?: string }): Promise<{ count: number }> => {
    const response = await api.post<{ count: number }>('/leave/requests/bulk-reject', data);
    return response.data;
  },

  getLeaveBalance: async (params?: { employeeId?: string; leavePeriodId?: string }): Promise<LeaveBalanceDto[]> => {
    const response = await api.get<LeaveBalanceDto[]>('/leave/balance', { params });
    return response.data;
  },

  getWhoIsOnLeave: async (params?: { date?: string; teamOnly?: boolean }): Promise<WhoIsOnLeaveDto[]> => {
    const response = await api.get<WhoIsOnLeaveDto[]>('/leave/on-leave', { params });
    return response.data;
  },

  exportLeaveRequests: async (params?: {
    employeeId?: string;
    leaveTypeId?: string;
    leavePeriodId?: string;
    status?: number;
    fromDate?: string;
    toDate?: string;
    searchTerm?: string;
    teamOnly?: boolean;
  }): Promise<Blob> => {
    const response = await api.get('/leave/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // ─── Assign Leave ─────────────────────────────────────────
  assignLeave: async (data: AssignLeave): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/leave/assign', data);
    return response.data;
  },

  // ─── Holidays ─────────────────────────────────────────────
  getHolidays: async (params?: { year?: number; searchTerm?: string }): Promise<HolidayDto[]> => {
    const response = await api.get<HolidayDto[]>('/leave/holidays', { params });
    return response.data;
  },

  createHoliday: async (data: CreateHoliday): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/leave/holidays', data);
    return response.data;
  },

  updateHoliday: async (id: string, data: UpdateHoliday): Promise<void> => {
    await api.put(`/leave/holidays/${id}`, data);
  },

  deleteHoliday: async (id: string): Promise<void> => {
    await api.delete(`/leave/holidays/${id}`);
  },

  // ─── Leave Usage Report ───────────────────────────────────
  getLeaveUsageReport: async (params?: {
    leavePeriodId?: string;
    departmentId?: string;
    employeeId?: string;
    searchTerm?: string;
  }): Promise<LeaveUsageReportDto[]> => {
    const response = await api.get<LeaveUsageReportDto[]>('/leave/usage-report', { params });
    return response.data;
  },
};
